import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
  InputField,
  Alert,
  AlertTitle,
  AlertDescription,
} from "@jem-open/jem-ui";
import { MapPinOff } from "lucide-react";
import { AttendanceException } from "../services/model/AttendanceException";
import {
  getExceptionShiftLabel,
  getShiftTimeRange,
} from "../utils/exceptionDisplayUtils";
import {
  computeGeofenceAdjustedTime,
  formatClockTime24,
  formatGeofenceDistance,
  formatGeofenceTimestamp,
  GEOFENCE_DISTANCE_CAP_METERS,
  GEOFENCE_WALKING_SPEED_M_PER_MIN,
  isGeofenceBeyondCap,
  parseClockTime,
} from "../utils/geofenceUtils";

interface GeofenceActionModalProps {
  exception: AttendanceException;
  open: boolean;
  onClose: () => void;
  onResolve: (id: string, action: string, reason?: string) => void;
}

const GeofenceActionModal: React.FC<GeofenceActionModalProps> = ({
  exception,
  open,
  onClose,
  onResolve,
}) => {
  const rawDirection = exception.geofence_direction;
  const rawDistance = exception.geofence_distance_meters;
  const missingFields = rawDirection == null || rawDistance == null;
  // Display defaults only — never pass these to compute logic when fields are missing.
  const direction = rawDirection ?? "in";
  const distance = rawDistance ?? 0;
  const beyondCap = !missingFields && isGeofenceBeyondCap(distance);
  const estimate =
    missingFields || beyondCap || !exception.timestamp
      ? null
      : computeGeofenceAdjustedTime(exception.timestamp, direction, distance);
  const showManualReview = missingFields || beyondCap;
  const displayTimestamp = exception.timestamp
    ? formatGeofenceTimestamp(exception.timestamp)
    : "";

  // 24-hour "HH:MM" seed for the native time input: prefer the walking
  // estimate (already 24h), else the original event time, else blank.
  const timestampMinutes = exception.timestamp
    ? parseClockTime(exception.timestamp)
    : null;
  const fallbackTimeValue =
    timestampMinutes === null ? "" : formatClockTime24(timestampMinutes);
  const defaultAdjustedTime = showManualReview
    ? ""
    : estimate?.adjusted ?? fallbackTimeValue;

  const [action, setAction] = useState<"confirm" | "no-show">("confirm");
  const [adjustedTime, setAdjustedTime] = useState(defaultAdjustedTime);
  const [reason, setReason] = useState("");

  // Reset per-exception when the modal opens against a new row.
  useEffect(() => {
    setAdjustedTime(defaultAdjustedTime);
    setAction("confirm");
    setReason("");
  }, [exception.id, open, defaultAdjustedTime]);

  const directionLabel = direction === "in" ? "clock-in" : "clock-out";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="w-[calc(100vw_-_1rem)] max-w-none sm:w-full sm:max-w-[520px] max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-xs">
            <MapPinOff size={18} className="text-red-500" />
            Geofence Violation — Review
          </DialogTitle>
          <DialogDescription>
            {exception.employee_name} clocked{" "}
            {direction === "in" ? "in" : "out"}{" "}
            {formatGeofenceDistance(distance)} outside the{" "}
            {exception.location_name} geofence.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-md py-sm">
          <div className="rounded-lg border border-greyscale-border p-sm space-y-xxs">
            <p className="text-sm">
              <span className="font-semibold">Employee:</span>{" "}
              {exception.employee_name}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Site:</span>{" "}
              {exception.location_name} — {exception.region_name}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Shift:</span>{" "}
              {getExceptionShiftLabel(exception)}
              {exception.shift_start && exception.shift_end
                ? ` (${getShiftTimeRange(
                    exception.shift_start,
                    exception.shift_end
                  )})`
                : ""}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Original {directionLabel}:</span>{" "}
              {displayTimestamp || "—"}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Distance outside:</span>{" "}
              {formatGeofenceDistance(distance)}
            </p>
          </div>

          {showManualReview && (
            <Alert variant="destructive">
              <AlertTitle>Manual review required</AlertTitle>
              <AlertDescription>
                {missingFields ? (
                  <>
                    Geofence direction or distance is missing on this exception,
                    so no walking-time estimate can be offered. Set the{" "}
                    {directionLabel} time manually below, or mark as No Show.
                  </>
                ) : (
                  <>
                    {formatGeofenceDistance(distance)} is beyond the{" "}
                    {formatGeofenceDistance(GEOFENCE_DISTANCE_CAP_METERS)}{" "}
                    walking-estimate cap. The employee may be in a vehicle, at
                    the wrong site, or the GPS may be malfunctioning. Set the{" "}
                    {directionLabel} time manually below, or mark as No Show.
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-xs">
            <Label className="text-sm font-semibold">Action</Label>
            <RadioGroup
              value={action}
              onValueChange={(v) => setAction(v as "confirm" | "no-show")}
            >
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="confirm" id="geo-confirm" />
                <Label htmlFor="geo-confirm" className="text-sm">
                  Confirm {directionLabel} time
                </Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="no-show" id="geo-noshow" />
                <Label htmlFor="geo-noshow" className="text-sm">
                  Mark as No Show
                </Label>
              </div>
            </RadioGroup>
          </div>

          {action === "confirm" && (
            <div className="space-y-xxs">
              <Label
                htmlFor="geo-adjusted-time"
                className="text-sm font-semibold"
              >
                Adjusted {directionLabel} time
              </Label>
              <InputField
                id="geo-adjusted-time"
                type="time"
                value={adjustedTime}
                onChange={(e) => setAdjustedTime(e.target.value)}
              />
              {estimate ? (
                <p className="text-xs text-greyscale-text-caption">
                  Estimated from {displayTimestamp || exception.timestamp}{" "}
                  {direction === "in" ? "+" : "−"} {estimate.deltaMinutes} min
                  walking time at {GEOFENCE_WALKING_SPEED_M_PER_MIN} m/min (
                  {formatGeofenceDistance(distance)}). Edit if you have better
                  information.
                </p>
              ) : (
                <p className="text-xs text-greyscale-text-caption">
                  No estimate offered — set the time manually based on what you
                  know about this clock event.
                </p>
              )}
            </div>
          )}

          {action === "no-show" && (
            <div className="space-y-xxs">
              <Label htmlFor="geo-reason" className="text-sm font-semibold">
                Reason
              </Label>
              <Textarea
                id="geo-reason"
                placeholder="Provide a reason for marking as no show..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-full sm:w-auto">
              Cancel
            </Button>
          </DialogClose>
          <Button
            disabled={
              (action === "confirm" && !adjustedTime.trim()) ||
              (action === "no-show" && !reason.trim())
            }
            variant={action === "no-show" ? "destructive" : "default"}
            className="w-full sm:w-auto"
            onClick={() => {
              if (action === "confirm") {
                onResolve(exception.id, "clock_override", adjustedTime);
              } else {
                onResolve(exception.id, "no_show", reason);
              }
              onClose();
            }}
          >
            {action === "confirm" ? "Confirm clock time" : "Confirm No Show"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GeofenceActionModal;
